import { gql } from '@apollo/client'
import { Button } from '@farol-ds/react'
import { DocSearchElement, DocToolbar, GA4EventProps } from '@jusbrasil-web/docview/core'
import { useAppBarContext } from '@jusbrasil-web/shared-appbar'
import { useOnScreen } from '@jusbrasil-web/shared-hooks'
import {
  buildWhatsappRedirect,
  SEUS_DIREITOS_ARTIGOS_MESSAGE,
} from '@jusbrasil-web/shared-seus-direitos'
import { useUserSession } from '@jusbrasil-web/shared-user-session'
import classNames from 'classnames'
import { RefObject, useEffect, useRef } from 'react'

import { useSegmentation } from '../../../hooks/segmentation-hook'
import { CommentButton } from '../../comment-button'
import { LikeDocumentButton } from '../../like-document-button'
import {
  ArticleNewsToolbar_DocumentFragment,
  ArticleNewsToolbar_ViewerFragment,
} from './__generated__/article-news-toolbar.graphql'
import styles from './article-news-toolbar.module.scss'

interface SendMetricsType {
  (name: string, properties?: Record<string, unknown>): void
}

export interface ArticleToolbarProps {
  document: ArticleNewsToolbar_DocumentFragment
  viewer: ArticleNewsToolbar_ViewerFragment | null
  docSearchRef: RefObject<DocSearchElement>
  ga4EventData: GA4EventProps
  sendMetrics?: SendMetricsType
  onToolbarStickyChange?: (sticky: boolean) => void
  fullCta?: boolean
}

export function ArticleNewsToolbar(props: ArticleToolbarProps) {
  const {
    document,
    viewer,
    docSearchRef,
    ga4EventData,
    sendMetrics,
    onToolbarStickyChange,
    fullCta,
  } = props

  const segmentationData = useSegmentation()
  const { ctaText, ctaUrl } = segmentationData || {}
  const { stickyTriggerRef: toolBarRefElement } = useAppBarContext()
  const toolbarSticky = !useOnScreen(toolBarRefElement, { threshold: 0 }, true)
  const anchorEventSentRef = useRef(false)
  const session = useUserSession()

  const isPesquisaJuridicaSegment = segmentationData?.typeSegmentation === 'PESQUISA_JURIDICA'
  const isSeusDireitosSegment = segmentationData?.typeSegmentation === 'SEUS_DIREITOS'

  const finalUrl = isSeusDireitosSegment
    ? buildWhatsappRedirect({
        message: SEUS_DIREITOS_ARTIGOS_MESSAGE,
        deviceId: session?.jdid,
        userId: session?.pid != null ? String(session.pid) : undefined,
      })
    : ctaUrl

  useEffect(() => {
    if (onToolbarStickyChange && !anchorEventSentRef.current) {
      onToolbarStickyChange(toolbarSticky)
      anchorEventSentRef.current = toolbarSticky
    }
  }, [toolbarSticky, onToolbarStickyChange, anchorEventSentRef])

  const getKindButton = () => {
    if (segmentationData?.typeSegmentation === 'PESQUISA_JURIDICA') {
      return 'secondary'
    }
    return 'primary'
  }

  function onClickCta() {
    if (segmentationData?.typeSegmentation === 'SEUS_DIREITOS' && sendMetrics) {
      const metadata = {
        copy: ctaText,
        page: 'docview-artigo',
        action: 'open_chat',
        position: toolbarSticky ? 'anchor' : 'header',
      }

      sendMetrics('SeusDireitos.CtaClick', { metadata })
      sendMetrics('SeusDireitos.QuestionSent', { metadata })
    }
  }

  return (
    <DocToolbar
      document={document}
      viewer={viewer}
      docSearchRef={docSearchRef}
      mainActions={
        <>
          {isPesquisaJuridicaSegment ? (
            <Button kind="primary" asChild>
              <a
                target="_blank"
                href="https://jusbrasilpesquisa.typeform.com/to/Si8TKsJU?utm_source=docview-news"
                rel="noreferrer"
              >
                Assinar Jusbrasil News
              </a>
            </Button>
          ) : segmentationData ? (
            <Button
              kind={getKindButton()}
              className={classNames({
                [styles.ctaFullWidth]: fullCta && isSeusDireitosSegment,
              })}
              asChild
            >
              <a onClick={onClickCta} target="_blank" href={finalUrl} rel="noreferrer">
                {ctaText}
              </a>
            </Button>
          ) : (
            <>
              <LikeDocumentButton document={document} ga4EventData={ga4EventData} />
              <CommentButton ga4EventData={ga4EventData} />
            </>
          )}
        </>
      }
      ga4DocumentSaveEventData={ga4EventData}
      ctaFullWidth={fullCta && isSeusDireitosSegment}
    />
  )
}

ArticleNewsToolbar.fragments = {
  document: gql`
    fragment ArticleNewsToolbar_document on Document {
      ...DocToolbar_document
      ...LikeDocumentButton_document
    }

    ${DocToolbar.fragments.document}
    ${LikeDocumentButton.fragments.document}
  `,

  viewer: gql`
    fragment ArticleNewsToolbar_viewer on Profile {
      ...DocToolbar_viewer
    }

    ${DocToolbar.fragments.viewer}
  `,
}
